import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Box, Button, List, ListItem, Menu, MenuItem, styled, Typography, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { useNavigate } from "react-router-dom";
import { navigateToBlog } from "../../utils/blogUtils";

const TopicGenerateBoxOne = styled(Box)(({ theme }) => ({
  "&.boxHeight": {
    height: "100%",
  },
  "& .selectTitle": {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& h4": {
      fontSize: "30px",
      color: theme.palette.text.primary,
      fontWeight: 700,
      lineHeight: "34px",
      textAlign: "center",
    },
  },
  "& .CopyBtnWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "15px",
    "& button": {
      "& svg": {
        fontSize: "16px",
        marginRight: "5px",
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        color: "#fff",
      },

      "&:focus": {
        outline: "0",
      },
    },
    "& .CollapseIcon": {
      fontSize: "35px",
      cursor: "pointer",
    },
  },
  "& ul": {
    padding: "0",
    "& li": {
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: "10px",
      marginBottom: "15px",
      padding: "22px 18px 14px",
      position: "relative",
      "& .TopicsWrapper": {
        flex: "1",
        "&::selection": {
          userSelect: "none",
        },
        "& .dangerouslySetInnerHTML": {
          marginTop: "40px",
          height: "calc(100vh - 300px)",
          overflow: "auto",
          "& *": {
            color: theme.palette.text.primary,
          },
          "&::-webkit-scrollbar-track": {
            WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
            borderRadius: "10px",
            backgroundColor: "#F5F5F5",
          },
          "&::-webkit-scrollbar": { width: "5px", backgroundColor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "10px",
            WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,.3)",
            backgroundColor: theme.palette.primary.main,
          },
        },
        "& .wordCount": {
          "& h3": {
            color: theme.palette.text.primary,
            fontWeight: 700,
          },
          border: `1px solid ${theme.palette.primary.main}`,
          padding: "7px 20px",
          borderRadius: "50px",
        },
        "& h4": {
          fontSize: "16px",
          fontWeight: 700,
          marginBottom: "8px",
        },
        "& h2": {
          fontSize: "22px",
          fontWeight: 700,
          marginBottom: "30px",
          marginTop: "20px",
        },
        "& p": {
          fontSize: "14px",
          marginBottom: "15px",
          "& .ReadMoreLess": {
            color: theme.palette.primary.main,
            cursor: "pointer",
          },
        },
        "& .GenerateCountWrapper": {
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gap: "15px",
          "& button": {
            minWidth: "94px",
            minHeight: "36.13px",
          },
        },
        "& .TopIconBarWrapper": {
          position: "absolute",
          top: "10px",
          left: "0",
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "0 10px",
          "& .UpDownIcon": {
            backgroundColor: theme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": {
              fontSize: "20px",
              marginRight: "5px",
              cursor: "pointer",
            },
          },
          "& .MinusIcon": {
            backgroundColor: theme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": {
              fontSize: "24px",
              cursor: "pointer",
            },
          },
        },
        "& .BottomIconBarWrapper": {
          position: "absolute",
          bottom: "3px",
          right: "5px",
          "& svg": {
            cursor: "pointer",
            "&.LoadingAnimated": {
              animation: "RotateAnimated 1s linear infinite",
              "@keyframes RotateAnimated": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            },
          },
        },
      },
    },
  },
}));

function DragDropList({ blog, collapse, handleCollapse, formState, items, isDisable, setIsDisable, handleRemoveItem = () => null }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(false);
  const theme = useTheme();

  const handleMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(false);
  };

  const handleMoreDelete = item => {
    handleRemoveItem(item);
    setAnchorEl(false);
  };

  useEffect(() => {
    if (items.length < 1) {
      setIsDisable(true);
    } else {
      setIsDisable(false);
    }
  }, [items]);

  return (
    <TopicGenerateBoxOne className={items.length === 0 && "boxHeight"}>
      {items.length > 0 && (
        <Box className="CopyBtnWrapper">
          <Button disabled={isDisable} onClick={() => navigateToBlog("blog", blog, formState, navigate)}>
            Complete
          </Button>
          {collapse ? (
            <ArrowCircleLeftIcon onClick={handleCollapse} className="CollapseIcon" titleAccess="Show Topics Bar" />
          ) : (
            <ArrowCircleRightIcon onClick={handleCollapse} className="CollapseIcon" titleAccess="Hide Topics Bar" />
          )}
        </Box>
      )}
      {items.length > 0 ? (
        <List>
          {items.map(item => (
            <ListItem key={item.id}>
              <Box className="TopicsWrapper">
                <Box className="dangerouslySetInnerHTML" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.blogContent) }}></Box>
                <Box className="TopIconBarWrapper">
                  <Box className="wordCount">
                    <Typography component="h3">
                      Word Count: <Typography component="span">{blog.wordCount}</Typography>
                    </Typography>
                  </Box>
                  <Box className="MinusIcon">
                    <MoreVertIcon onClick={handleMenu} titleAccess="More Option" />
                  </Box>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    sx={{
                      "& .MuiPopover-paper": {
                        overflow: "visible",
                        "&::before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 7,
                          width: 10,
                          height: 10,
                          bgcolor: theme.palette.background.default,
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderLeft: `1px solid ${theme.palette.divider}`,
                        },
                        "& ul": {
                          p: "0",
                          borderRadius: "6px",
                          overflow: "hidden",
                          "& .menuitem": {
                            backgroundColor: theme.palette.background.default,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            p: "12px 18px",
                            "& > span.MuiTouchRipple-root": {
                              display: "none",
                            },
                            "& .DeleteIconWrapper": {
                              flex: "1",
                              textAlign: "end",
                              "& button": {
                                width: "100%",
                                "& svg": {
                                  fontSize: "20px",
                                  marginRight: "3px",
                                },
                              },
                            },
                            "& .GenerateCountWrapper": {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "15px",
                              "& button": {
                                minHeight: "36px",
                                minWidth: "auto",
                                "& .LoadingAnimated": {
                                  animation: "RotateAnimated 1s linear infinite",
                                  "@keyframes RotateAnimated": {
                                    "0%": { transform: "rotate(0deg)" },
                                    "100%": { transform: "rotate(360deg)" },
                                  },
                                },
                              },
                            },
                          },
                        },
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={anchorEl}
                    onClose={handleClose}
                  >
                    <MenuItem className="menuitem" title="Delete This List">
                      <Box className="DeleteIconWrapper">
                        <Button onClick={() => handleMoreDelete(item)} title="Delete This List">
                          <DeleteIcon /> Delete
                        </Button>
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box className="selectTitle">
          <Typography component="h4">Please select a title</Typography>
        </Box>
      )}
    </TopicGenerateBoxOne>
  );
}

export default DragDropList;
